import * as THREE from "three";
import type { PowerKind } from "../../data/model";
import { G, Kit, col, hdr, instance, once, ramp, revolve, slab, spring, profileSlab, solGradient } from "./kit";
import { decalMaterial, glowSprite } from "./fx";
import { DISPLAY_FONT, canvasTex } from "./tex";
import { text3D } from "./text";
import { buildHoverBoard } from "./boards";

/**
 * Floating collectibles. Every pickup: origin = the icon's centre (the scene sets y = yBase + 0.8), a child "float"
 * (bobs) holding the glow sprite and a child "spin" (turns about y) holding the item and its glowing outline (an
 * inverted hull in the pickup's colour). `animateProp` bobs, spins and flickers them — don't rotate the group itself.
 */

export const PICKUP_COLORS: Record<string, number> = {
  magnet: 0xff3b55, x2: 0xa45bff, sneakers: 0xffb400, jetpack: 0x2ea8ff, pogo: 0xff7a1a, mystery: 0xd36bff,
  headstart: 0x16c3d8, booster: 0x22c55e, board: 0xff5fd2, shield: 0x3b7ddd,
  key: 0xffc53d, mysteryBox: 0xb57bff, letter: 0xffffff, token: 0x19e08f
};

const outlineMaterial = (color: number): THREE.Material => once(`mat:outline:${color}`, () => new THREE.MeshBasicMaterial({
  color: hdr(color, 1.5), side: THREE.BackSide, toneMapped: false, transparent: true, opacity: 0.9, depthWrite: false
}));

/** Centre an item on the origin (so the hull scales around its middle). */
function centred(item: THREE.Object3D): THREE.Object3D {
  const box = new THREE.Box3().setFromObject(item);
  const c = box.getCenter(new THREE.Vector3());
  const holder = new THREE.Group();
  item.position.sub(c);
  holder.add(item);
  return holder;
}

/**
 * `wobble`: sway ±0.5 rad instead of spinning — for lettering, which must never show its mirrored back to the player.
 * `turn`: the item's resting yaw (its best side towards the oncoming camera).
 */
function frame(key: string, color: number, make: () => THREE.Object3D, opts: { hull?: number; glow?: number; tilt?: number; wobble?: boolean; turn?: number } = {}): THREE.Group {
  return instance(`pickup:${key}`, () => {
    const item = centred(make());
    if (opts.tilt) item.rotation.z = opts.tilt;
    if (opts.turn) item.rotation.y = opts.turn;
    const spin = new THREE.Group();
    spin.name = "spin";
    spin.add(item);
    // the glowing outline: the same meshes, slightly bigger, back faces only, unlit
    const hull = item.clone(true);
    hull.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) mesh.material = outlineMaterial(color);
      if ((o as THREE.Sprite).isSprite || o.name.startsWith("flame")) o.visible = false;
    });
    const k = opts.hull ?? 1.07;
    hull.scale.multiplyScalar(k);
    hull.name = "hull";
    hull.renderOrder = -1;
    spin.add(hull);
    const float = new THREE.Group();
    float.name = "float";
    float.add(glowSprite(color, opts.glow ?? 2.3, 0.5, 0.75), spin);
    const root = new THREE.Group();
    root.name = key;
    root.userData.anim = "pickup";
    if (opts.wobble) root.userData.wobble = true;
    root.add(float);
    return root;
  });
}

// ------------------------------------------------------------------------------------------------ items

function magnet(): THREE.Object3D {
  const kit = new Kit();
  const red = 0xff2d4a;
  kit.add(G.torus(0.3, 0.135, 12, 22, Math.PI), "gloss", red);
  for (const x of [-0.3, 0.3]) {
    kit.add(G.cyl(0.135, 0.135, 0.34, 18), "gloss", red, { p: [x, -0.17, 0] });
    kit.add(G.cyl(0.138, 0.138, 0.2, 18), "chrome", 0xf0f3f8, { p: [x, -0.44, 0] });
  }
  kit.add(G.torus(0.3, 0.138, 12, 22, Math.PI * 0.22), "paint", 0xffffff, { r: [0, 0, Math.PI * 0.39] });
  return kit.build();
}

function lettering(text: string, size: number, front: number | THREE.Color, side: number | THREE.Color, depth = 0.22): THREE.Object3D {
  const kit = new Kit();
  const f = col(front), s = col(side);
  kit.add(text3D(text, size, depth, 0.035), "gloss", (_p, n) => (Math.abs(n.z) > 0.7 ? f : s), { r: [0, Math.PI, 0] });
  return kit.build();
}

function sneaker(): THREE.Object3D {
  const kit = new Kit();
  const upper = 0xffb400;
  kit.add(profileSlab([[-0.48, 0], [0.42, 0], [0.54, 0.06], [0.52, 0.16], [-0.5, 0.16]], 0.44, 0.05, { round: [2, 3], curveSegments: 4 }), "plastic", 0xf6f6f8);
  kit.add(profileSlab([[-0.47, 0.14], [0.44, 0.14], [0.52, 0.22], [0.36, 0.33], [0.04, 0.45], [-0.12, 0.66], [-0.42, 0.7], [-0.53, 0.5], [-0.5, 0.15]], 0.4, 0.08, { round: [2, 3, 4, 5, 6, 7], curveSegments: 4 }), "gloss", upper);
  // laces over the vamp, a white heel tab and side flash
  for (let i = 0; i < 3; i++) kit.add(G.box(0.3, 0.035, 0.07), "paint", 0xffffff, { p: [0, 0.42 + i * 0.07, 0.24 - i * 0.12], r: [0.5, 0, 0] });
  kit.add(G.box(0.12, 0.2, 0.06), "paint", 0xffffff, { p: [0, 0.6, -0.5] });
  for (const x of [-0.205, 0.205]) kit.add(G.box(0.01, 0.07, 0.5), "paint", 0xffffff, { p: [x, 0.26, -0.06], r: [0.35, 0, 0] });
  // the spring under the sole
  kit.add(spring(0.17, 0.04, 4, 0.4, 14, 6), "chrome", 0xe8ecf2, { p: [0, -0.4, -0.05] });
  kit.add(G.cyl(0.22, 0.22, 0.05, 16), "plastic", 0x2a2c35, { p: [0, -0.42, -0.05] });
  return kit.build();
}

function jetpack(): THREE.Object3D {
  const kit = new Kit();
  const blue = 0x1f8fff;
  for (const x of [-0.21, 0.21]) {
    kit.add(G.capsule(0.17, 0.52, 6, 16), "gloss", blue, { p: [x, 0, 0] });
    for (const y of [-0.2, 0.2]) kit.add(G.cyl(0.176, 0.176, 0.06, 16), "paint", 0xffffff, { p: [x, y, 0] });
    kit.add(G.cyl(0.05, 0.06, 0.1, 10), "chrome", 0xe8ecf2, { p: [x, 0.47, 0] });
    kit.add(revolve([[0.08, 0], [0.11, -0.07], [0.15, -0.2]], 16, { axis: "y", smooth: true }), "chrome", 0xdfe3ea, { p: [x, -0.4, 0] });
    kit.add(revolve([[0.14, -0.2], [0.1, -0.08], [0.07, 0]], 16, { axis: "y" }), "satin", 0x3a2a24, { p: [x, -0.4, 0] });
    kit.add(G.box(0.06, 0.62, 0.03), "plastic", 0x22242c, { p: [x, 0.02, 0.27] });
  }
  kit.add(G.chamfer(0.66, 0.74, 0.09, 0.03), "satin", 0x3a3f4f, { p: [0, 0.02, 0.2] });
  const group = kit.build();
  // flames (own meshes so they flicker)
  const flameMat = once("mat:flame", () => new THREE.MeshBasicMaterial({ color: hdr(0xff7a1a, 1.1), transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
  const coreMat = once("mat:flamecore", () => new THREE.MeshBasicMaterial({ color: hdr(0xffd36b, 1.3), toneMapped: false }));
  for (const [i, x] of [-0.21, 0.21].entries()) {
    const flame = new THREE.Group();
    flame.name = `flame${i}`;
    const outer = new THREE.Mesh(G.cyl(0.13, 0.0, 0.42, 12, true), flameMat);
    outer.position.y = -0.21;
    const core = new THREE.Mesh(G.cyl(0.07, 0.0, 0.24, 10, true), coreMat);
    core.position.y = -0.12;
    flame.add(outer, core);
    flame.position.set(x, -0.6, 0);
    group.add(flame);
  }
  return group;
}

function pogo(): THREE.Object3D {
  const kit = new Kit();
  const orange = 0xff7a1a;
  kit.add(G.cyl(0.035, 0.035, 1.02, 10), "chrome", 0xe6e9ef, { p: [0, 0.09, 0] });
  kit.add(G.cyl(0.035, 0.035, 0.64, 10), "chrome", 0xe6e9ef, { p: [0, 0.58, 0], r: [0, 0, Math.PI / 2] });
  for (const x of [-0.25, 0.25]) kit.add(G.cyl(0.058, 0.058, 0.2, 14), "rubber", 0x22232a, { p: [x, 0.58, 0], r: [0, 0, Math.PI / 2] });
  kit.add(G.cyl(0.09, 0.09, 0.42, 16), "gloss", orange, { p: [0, 0.12, 0] });
  kit.add(G.cyl(0.095, 0.095, 0.05, 16), "paint", 0xffffff, { p: [0, 0.3, 0] });
  for (const x of [-0.17, 0.17]) {
    kit.add(G.chamfer(0.22, 0.06, 0.14, 0.02), "gloss", orange, { p: [x, -0.12, 0] });
    kit.add(G.box(0.2, 0.012, 0.12), "rubber", 0x22232a, { p: [x, -0.085, 0] });
  }
  kit.add(spring(0.065, 0.02, 5, 0.3, 12, 5), "chrome", 0xe6e9ef, { p: [0, -0.44, 0] });
  kit.add(G.cyl(0.05, 0.065, 0.1, 12), "rubber", 0x22232a, { p: [0, -0.49, 0] });
  return kit.build();
}

function key(): THREE.Object3D {
  const kit = new Kit();
  const gold = 0xffc53d;
  kit.add(G.torus(0.25, 0.075, 12, 30), "metal", gold, { p: [0, 0.42, 0] });
  kit.add(new THREE.OctahedronGeometry(0.12), "gloss", 0x9945ff, { p: [0, 0.42, 0], s: [1, 1.25, 0.7] });
  kit.add(G.cyl(0.095, 0.095, 0.08, 16), "metal", gold, { p: [0, 0.13, 0] });
  kit.add(G.cyl(0.052, 0.052, 0.8, 12), "metal", gold, { p: [0, -0.27, 0] });
  for (const [y, w] of [[-0.4, 0.2], [-0.53, 0.14], [-0.64, 0.22]] as [number, number][]) kit.add(G.chamfer(w, 0.085, 0.08, 0.02), "metal", gold, { p: [0.05 + w / 2, y, 0] });
  return kit.build();
}

const questionTag = (): THREE.Texture => canvasTex("tag-question", 256, 256, (ctx, w, h) => {
  ctx.beginPath(); ctx.arc(w / 2, h / 2, 118, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff"; ctx.fill();
  ctx.lineWidth = 14; ctx.strokeStyle = "#14f195"; ctx.stroke();
  ctx.fillStyle = "#7a2cff";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = `900 170px ${DISPLAY_FONT}`;
  ctx.fillText("?", w / 2, h / 2 + 10);
});

function giftBox(): THREE.Object3D {
  const kit = new Kit();
  kit.add(G.round(0.9, 0.9, 0.9, 0.08, 2), "gloss", 0x8b46ff);
  kit.add(G.box(0.18, 0.925, 0.925), "gloss", 0x14f195);
  kit.add(G.box(0.925, 0.925, 0.18), "gloss", 0x14f195);
  for (const s of [-1, 1]) kit.add(G.torus(0.15, 0.05, 8, 18), "gloss", 0x14f195, { p: [s * 0.13, 0.55, 0], r: [0, 0, s * 0.6], s: [1, 0.75, 0.6] });
  kit.add(G.sphere(0.08, 12, 8), "gloss", 0x0fd884, { p: [0, 0.5, 0] });
  const tag = decalMaterial("tag-question", questionTag(), { roughness: 0.35 });
  for (const [r, p] of [[[0, Math.PI, 0], [0, 0, -0.468]], [[0, 0, 0], [0, 0, 0.468]], [[0, Math.PI / 2, 0], [0.468, 0, 0]], [[0, -Math.PI / 2, 0], [-0.468, 0, 0]]] as [[number, number, number], [number, number, number]][]) {
    kit.add(G.plane(0.46, 0.46), tag, 0xffffff, { p, r });
  }
  return kit.build();
}

function token(): THREE.Object3D {
  const kit = new Kit();
  const flat = (g: THREE.BufferGeometry) => { const c = g.clone(); c.computeVertexNormals(); return c; };
  kit.add(flat(revolve([[0, 0.08], [0.42, 0.08], [0.52, 0.0], [0.42, -0.08], [0, -0.08]], 6, { phase: Math.PI / 6 })), "metal", 0x12c27e);
  kit.add(flat(revolve([[0.33, 0.085], [0.4, 0.085]], 6, { phase: Math.PI / 6 })), "metal", 0xffc53d);
  const star = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 ? 0.11 : 0.25;
    if (i === 0) star.moveTo(Math.cos(a) * r, Math.sin(a) * r); else star.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  star.closePath();
  const starGeo = once("g:tokenstar", () => slab(star, 0.05, 0.015, 1, 1));
  kit.add(starGeo, "metal", 0xffd75e, { p: [0, 0, 0.09] });
  kit.add(starGeo, "metal", 0xffd75e, { p: [0, 0, -0.09], r: [0, Math.PI, 0] });
  return kit.build();
}

function shield(): THREE.Object3D {
  const s = new THREE.Shape();
  s.moveTo(0, 0.5); s.quadraticCurveTo(0.25, 0.42, 0.42, 0.44); s.lineTo(0.42, 0.05); s.quadraticCurveTo(0.4, -0.32, 0, -0.52);
  s.quadraticCurveTo(-0.4, -0.32, -0.42, 0.05); s.lineTo(-0.42, 0.44); s.quadraticCurveTo(-0.25, 0.42, 0, 0.5);
  const kit = new Kit();
  kit.add(slab(s, 0.18, 0.05, 8, 2), "gloss", 0x3b7ddd);
  kit.add(text3D("+", 0.5, 0.06, 0.015), "paint", 0xffffff, { p: [0, 0.0, 0.1] });
  return kit.build();
}

function rocket(): THREE.Object3D {
  const kit = new Kit();
  kit.add(revolve([[0, 0.62], [0.12, 0.45], [0.19, 0.18], [0.2, -0.25], [0.15, -0.38]], 16, { axis: "y", smooth: true }), "gloss", 0xf3f5f9);
  kit.add(G.cyl(0.202, 0.202, 0.08, 16), "gloss", 0x16c3d8, { p: [0, 0.22, 0] });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    kit.add(G.chamfer(0.03, 0.3, 0.22, 0.01), "gloss", 0x16c3d8, { p: [Math.sin(a) * 0.22, -0.25, Math.cos(a) * 0.22], r: [0, a, 0] });
  }
  kit.add(G.cyl(0.12, 0.0, 0.3, 12, true), "glow", hdr(0xffa23a, 1.8), { p: [0, -0.53, 0] });
  return kit.build();
}

function boardItem(): THREE.Object3D {
  const board = buildHoverBoard("zapper");
  board.getObjectByName("underlight")?.removeFromParent();
  board.scale.setScalar(0.27);
  board.rotation.x = -1.05;
  const holder = new THREE.Group();
  holder.add(board);
  return holder;
}

// ------------------------------------------------------------------------------------------------ public builders

/** `buildPowerPickup(power)` → a floating power-up (magnet, x2, sneakers, jetpack, pogo, mystery, board, …). */
export function buildPowerPickup(power: PowerKind | string): THREE.Group {
  const color = PICKUP_COLORS[power] ?? 0xd36bff;
  switch (power) {
    case "magnet": return frame("magnet", color, magnet, { tilt: -0.25 });
    case "x2": return frame("x2", color, () => lettering("2X", 0.62, 0xffffff, 0x9945ff), { wobble: true });
    case "sneakers": return frame("sneakers", color, sneaker, { turn: -Math.PI / 2 - 0.35 });
    case "jetpack": return frame("jetpack", color, jetpack);
    case "pogo": return frame("pogo", color, pogo, { tilt: 0.22 });
    case "board": return frame("board", color, boardItem, { hull: 1.06 });
    case "headstart": return frame("headstart", color, rocket, { tilt: 0.3 });
    case "booster": return frame("booster", color, () => lettering("+", 1.0, 0xffffff, 0x16a34a, 0.26), { wobble: true });
    case "shield": return frame("shield", color, shield);
    default: return frame("mystery", color, () => {
      const kit = new Kit();
      kit.add(text3D("?", 0.95, 0.26, 0.04), "gloss", (p, n) => (Math.abs(n.z) > 0.7 ? ramp([[0, "#ff7ad9"], [0.5, "#c58bff"], [1, "#7ff3ff"]], p.y + 0.5) : col(0x7a2cff)), { r: [0, Math.PI, 0] });
      return kit.build();
    }, { wobble: true });
  }
}

/** `buildKey()` → the golden key with a Solana-purple gem. */
export const buildKey = (): THREE.Group => frame("key", PICKUP_COLORS.key, key, { tilt: 0.35 });

/** `buildMysteryBox()` → a purple gift box, green ribbon and bow, "?" tags. Centred on its origin like the others. */
export const buildMysteryBox = (): THREE.Group => frame("mysteryBox", PICKUP_COLORS.mysteryBox, giftBox, { hull: 1.06, glow: 2.6 });

/** `buildLetter(letter)` → a Word Hunt letter: white faces, Solana-gradient sides. */
export const buildLetter = (letter: string): THREE.Group => {
  const ch = (letter || "A").toUpperCase().slice(0, 1);
  return frame(`letter:${ch}`, PICKUP_COLORS.letter, () => {
    const kit = new Kit();
    kit.add(text3D(ch, 0.86, 0.24, 0.035), "gloss", (p, n) => (Math.abs(n.z) > 0.7 ? col(0xffffff) : solGradient(p.y + 0.5)), { r: [0, Math.PI, 0] });
    return kit.build();
  }, { hull: 1.06, wobble: true });
};

/** `buildSeasonToken()` → an emerald hexagon medal with a gold rim and star. */
export const buildSeasonToken = (): THREE.Group => frame("token", PICKUP_COLORS.token, token);

/** `buildBoardPickup()` → a mini hover board floating in a pink glow. */
export const buildBoardPickup = (): THREE.Group => buildPowerPickup("board");
